/**
 * TG-style вертикальный rail папок — слева от sidebar'а на ПК.
 * Использует пак SVG-иконок (folderIcons.ts) + поддержка emoji.
 */
import { useMemo, useRef, useState, useEffect, type MouseEvent as RMouseEvent, type TouchEvent as RTouchEvent } from "react";
import { NewFolderModal } from "@/components/NewFolderModal";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  fetchChatFolders, createChatFolder, editChatFolder, deleteChatFolder,
  markChatRead,
  type ChatFolder, type ChatTopic,
} from "@/api/dashboard";
import { Icon } from "@/components/prototype/Icon";
import { Avatar } from "@/components/prototype/Avatar";
import { FOLDER_ICONS, parseFolderIcon, type FolderIconDef } from "@/data/folderIcons";
import { useAuth } from "@/auth/AuthContext";
import { useWorkspaceTrigger } from "@/lib/useWorkspaceTrigger";

function ProfileRailButton() {
  const { user } = useAuth();
  const ws = useWorkspaceTrigger();
  if (!user) return null;
  return (
    <button
      type="button"
      className="chat-folder-rail-profile"
      onClick={ws.wrapClick(() => document.dispatchEvent(new CustomEvent("kb-open-self-profile")))}
      title="Профиль · долгое нажатие / правый клик = переключить раздел"
      {...ws.handlers}
    >
      <Avatar
        name={user.display_name || "Я"}
        userId={user.id}
        avatarFilename={user.avatar_filename}
        size={42}
        color="#4fae4e"
      />
    </button>
  );
}

export function FolderRail({
  topics, activeFolderId, onChange, onOpenApps,
}: {
  topics: ChatTopic[];
  activeFolderId: number | null;
  onChange: (folderId: number | null) => void;
  onOpenApps?: () => void;
}) {
  const qc = useQueryClient();
  const [manageOpen, setManageOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [editFolder, setEditFolder] = useState<ChatFolder | null>(null);
  // folder=null означает «Все» — там доступен только пункт «Прочитать все»
  const [folderCtxMenu, setFolderCtxMenu] = useState<{ folder: ChatFolder | null; x: number; y: number } | null>(null);
  const deleteFolderMut = useMutation({
    mutationFn: (id: number) => deleteChatFolder(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/folders"] }),
    onError: (e: Error) => alert(`Не удалось удалить: ${e.message}`),
  });
  // «Прочитать все» — POST'им markChatRead для каждой непрочитанной темы (в папке или во всех).
  const markAllReadMut = useMutation({
    mutationFn: async (folder: ChatFolder | null) => {
      const targetIds = folder
        ? new Set(folder.topic_ids)
        : null;
      const unread = topics.filter(t => {
        if (t.unread_count <= 0) return false;
        if (!t.last_message) return false;
        if (targetIds && !targetIds.has(t.id)) return false;
        return true;
      });
      for (const t of unread) {
        try { await markChatRead(t.id, t.last_message!.id); } catch { /* ignore singleton */ }
      }
      return unread.length;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat/topics"] });
      qc.invalidateQueries({ queryKey: ["chat/folders"] });
    },
  });
  function openCtxMenu(folder: ChatFolder | null, x: number, y: number) {
    document.dispatchEvent(new CustomEvent("kb-close-ctx", { detail: { except: "rail" } }));
    setFolderCtxMenu({ folder, x, y });
  }
  useEffect(() => {
    if (!folderCtxMenu) return;
    const close = () => setFolderCtxMenu(null);
    const onCloseCtx = (e: Event) => {
      const ce = e as CustomEvent<{ except?: string }>;
      if (ce.detail?.except !== "rail") setFolderCtxMenu(null);
    };
    document.addEventListener("kb-close-ctx", onCloseCtx as EventListener);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("kb-close-ctx", onCloseCtx as EventListener);
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [folderCtxMenu]);

  const foldersQ = useQuery({
    queryKey: ["chat/folders"],
    queryFn: fetchChatFolders,
    staleTime: 60_000,
  });
  const folders = foldersQ.data ?? [];

  const unreadByTopic = useMemo(() => {
    const m = new Map<number, number>();
    for (const t of topics) m.set(t.id, t.unread_count ?? 0);
    return m;
  }, [topics]);

  const totalUnread = useMemo(
    () => topics.reduce((s, t) => s + (t.unread_count ?? 0), 0),
    [topics]
  );

  function folderUnread(f: ChatFolder): number {
    return f.topic_ids.reduce((s, tid) => s + (unreadByTopic.get(tid) ?? 0), 0);
  }

  return (
    <aside className="chat-folder-rail">
      {onOpenApps && (
        <button
          type="button"
          className="chat-apps-menu-btn"
          onClick={onOpenApps}
          title="Приложения"
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M5 7.2h9M5 12h14M5 16.8h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="18.6" cy="6.2" r="2.8" fill="#3ea6ff"/>
          </svg>
        </button>
      )}
      <RailItem
        svgPath="M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z"
        label="Все"
        unread={totalUnread}
        active={activeFolderId === null}
        onClick={() => onChange(null)}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openCtxMenu(null, e.clientX, e.clientY);
        }}
        onLongPress={(x, y) => openCtxMenu(null, x, y)}
      />
      {folders.map(f => {
        const parsed = parseFolderIcon(f.icon);
        return (
          <RailItem
            key={f.id}
            svgPath={parsed.kind === "svg" ? parsed.def.path : undefined}
            emoji={parsed.kind === "emoji" ? parsed.emoji : undefined}
            label={f.name}
            unread={folderUnread(f)}
            active={activeFolderId === f.id}
            onClick={() => onChange(f.id)}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openCtxMenu(f, e.clientX, e.clientY);
            }}
            onLongPress={(x, y) => openCtxMenu(f, x, y)}
          />
        );
      })}
      <button
        type="button"
        className="chat-folder-rail-add"
        onClick={() => setNewFolderOpen(true)}
        title="Создать / редактировать папки"
      >
        <Icon name="plus" size={16}/>
      </button>

      <ProfileRailButton/>

      {newFolderOpen && (
        <NewFolderModal
          topics={topics}
          onClose={() => setNewFolderOpen(false)}
        />
      )}
      {editFolder && (
        <NewFolderModal
          topics={topics}
          editFolder={editFolder}
          onClose={() => setEditFolder(null)}
        />
      )}
      {folderCtxMenu && (
        <div
          className="folder-ctx-menu"
          style={{ position: "fixed", top: folderCtxMenu.y, left: folderCtxMenu.x, zIndex: 1500 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" className="folder-ctx-item" onClick={() => {
            const f = folderCtxMenu.folder;
            setFolderCtxMenu(null);
            markAllReadMut.mutate(f);
          }}>
            <Icon name="check" size={16}/> Прочитать все
          </button>
          {folderCtxMenu.folder && <>
            <button type="button" className="folder-ctx-item" onClick={() => {
              setEditFolder(folderCtxMenu.folder!);
              setFolderCtxMenu(null);
            }}>
              <Icon name="edit" size={16}/> Редактировать
            </button>
            <button type="button" className="folder-ctx-item is-danger" onClick={() => {
              const f = folderCtxMenu.folder!;
              setFolderCtxMenu(null);
              if (window.confirm(`Удалить папку «${f.name}»? Чаты сами по себе не будут удалены.`)) {
                deleteFolderMut.mutate(f.id);
              }
            }}>
              <Icon name="trash" size={16}/> Удалить
            </button>
          </>}
        </div>
      )}
      {manageOpen && (
        <ManageFoldersModal
          folders={folders}
          onClose={() => {
            setManageOpen(false);
            qc.invalidateQueries({ queryKey: ["chat/folders"] });
          }}
        />
      )}
    </aside>
  );
}

function RailItem({
  svgPath, emoji, label, unread, active, onClick, onContextMenu, onLongPress,
}: {
  svgPath?: string;
  emoji?: string;
  label: string;
  unread: number;
  active: boolean;
  onClick: () => void;
  onContextMenu?: (e: RMouseEvent) => void;
  /** Долгий тап (500мс) на мобиле — координаты пальца чтобы расположить меню. */
  onLongPress?: (x: number, y: number) => void;
}) {
  // Long-press для мобилки: 500мс на месте → срабатывает; движение >8px = отмена (скролл).
  const longPressTimerRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  function clearLongPress() {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }
  function handleTouchStart(e: RTouchEvent) {
    if (!onLongPress) return;
    const t = e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY };
    longPressFiredRef.current = false;
    clearLongPress();
    longPressTimerRef.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      onLongPress(t.clientX, t.clientY);
    }, 500);
  }
  function handleTouchMove(e: RTouchEvent) {
    if (!touchStartRef.current) return;
    const t = e.touches[0];
    if (!t) return;
    const dx = Math.abs(t.clientX - touchStartRef.current.x);
    const dy = Math.abs(t.clientY - touchStartRef.current.y);
    if (dx > 8 || dy > 8) clearLongPress();
  }
  function handleTouchEnd() {
    clearLongPress();
    touchStartRef.current = null;
  }
  function handleClick() {
    // Если только что отработал long-press — не фаерим обычный клик.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    onClick();
  }
  return (
    <button
      type="button"
      onContextMenu={onContextMenu}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      className={`chat-folder-rail-item${active ? " is-active" : ""}`}
      title={label}
    >
      <span className="chat-folder-rail-icon">
        {svgPath ? (
          <svg viewBox="0 0 24 24" width="22" height="22">
            <path fill="currentColor" d={svgPath}/>
          </svg>
        ) : emoji ? (
          <span style={{ fontSize: 22, lineHeight: 1 }}>{emoji}</span>
        ) : (
          <Icon name="file" size={20}/>
        )}
        {unread > 0 && (
          <span className="chat-folder-rail-badge">{unread > 99 ? "99+" : unread}</span>
        )}
      </span>
      <span className="chat-folder-rail-label">{label}</span>
    </button>
  );
}


/* ── Manage modal: создать/переименовать/удалить + выбор SVG-иконки ── */

function ManageFoldersModal({ folders, onClose }: {
  folders: ChatFolder[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");
  const [newIcon, setNewIcon] = useState<string>("svg:folder");
  const [pickerOpenFor, setPickerOpenFor] = useState<number | "new" | null>(null);

  const createMut = useMutation({
    mutationFn: () => createChatFolder({ name: newName.trim(), icon: newIcon || null }),
    onSuccess: () => {
      setNewName(""); setNewIcon("svg:folder"); setPickerOpenFor(null);
      qc.invalidateQueries({ queryKey: ["chat/folders"] });
    },
    onError: (e: Error) => alert(`Не удалось создать: ${e.message}`),
  });
  const editMut = useMutation({
    mutationFn: (vars: { id: number; name: string; icon: string | null }) =>
      editChatFolder(vars.id, { name: vars.name, icon: vars.icon }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/folders"] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteChatFolder(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/folders"] }),
  });

  function renderIconPreview(icon: string | null): JSX.Element {
    const parsed = parseFolderIcon(icon);
    if (parsed.kind === "svg") {
      return (
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d={parsed.def.path}/>
        </svg>
      );
    }
    if (parsed.kind === "emoji") return <span style={{ fontSize: 20, lineHeight: 1 }}>{parsed.emoji}</span>;
    return <Icon name="file" size={18}/>;
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1500,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(480px, 92vw)", maxHeight: "90vh",
          background: "var(--paper)", borderRadius: 12,
          boxShadow: "0 12px 48px rgba(0,0,0,0.28)",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 16px", borderBottom: "1px solid var(--line)",
        }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Папки чатов</h3>
          <button
            type="button" onClick={onClose} title="Закрыть"
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, color: "var(--mute)" }}
          >
            <Icon name="x" size={16}/>
          </button>
        </div>

        <div style={{ padding: 16, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
          {folders.length === 0 && (
            <div style={{ color: "var(--mute)", padding: 8 }}>Пока нет ни одной папки</div>
          )}
          {folders.map(f => (
            <div key={f.id} style={{
              padding: "6px 0", borderBottom: "1px solid var(--line)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setPickerOpenFor(pickerOpenFor === f.id ? null : f.id)}
                  style={{
                    width: 40, height: 40, display: "grid", placeItems: "center",
                    background: "var(--paper-2)", border: "1px solid var(--line)",
                    borderRadius: 8, cursor: "pointer", color: "var(--brick)",
                  }}
                  title="Сменить иконку"
                >
                  {renderIconPreview(f.icon)}
                </button>
                <input
                  className="input"
                  defaultValue={f.name}
                  style={{ flex: 1 }}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== f.name) editMut.mutate({ id: f.id, name: v, icon: f.icon });
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`Удалить папку «${f.name}»?`)) deleteMut.mutate(f.id);
                  }}
                  title="Удалить"
                  style={{
                    background: "transparent", border: "none", cursor: "pointer",
                    padding: 6, color: "var(--brick)",
                  }}
                >
                  <Icon name="trash" size={14}/>
                </button>
              </div>
              {pickerOpenFor === f.id && (
                <IconPickerGrid
                  current={f.icon}
                  onPick={(icon) => {
                    editMut.mutate({ id: f.id, name: f.name, icon });
                    setPickerOpenFor(null);
                  }}
                />
              )}
            </div>
          ))}

          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            <span style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase" }}>
              Новая папка
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => setPickerOpenFor(pickerOpenFor === "new" ? null : "new")}
                style={{
                  width: 40, height: 40, display: "grid", placeItems: "center",
                  background: "var(--paper-2)", border: "1px solid var(--line)",
                  borderRadius: 8, cursor: "pointer", color: "var(--brick)",
                }}
              >
                {renderIconPreview(newIcon)}
              </button>
              <input
                className="input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Название"
                style={{ flex: 1 }}
                onKeyDown={(e) => { if (e.key === "Enter" && newName.trim()) createMut.mutate(); }}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={!newName.trim() || createMut.isPending}
                onClick={() => createMut.mutate()}
              >
                Создать
              </button>
            </div>
            {pickerOpenFor === "new" && (
              <IconPickerGrid current={newIcon} onPick={(icon) => { setNewIcon(icon); setPickerOpenFor(null); }}/>
            )}
          </div>
        </div>

        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 8,
          padding: "12px 16px", borderTop: "1px solid var(--line)",
        }}>
          <button type="button" className="btn" onClick={onClose}>Готово</button>
        </div>
      </div>
    </div>
  );
}


/* ── Icon picker grid ──────────────────────────────────────── */

function IconPickerGrid({ current, onPick }: {
  current: string | null;
  onPick: (icon: string) => void;
}) {
  const byCat: Record<string, FolderIconDef[]> = {};
  for (const i of FOLDER_ICONS) {
    byCat[i.category] = byCat[i.category] || [];
    byCat[i.category].push(i);
  }
  const catLabels: Record<string, string> = {
    main: "Основные",
    people: "Люди",
    work: "Работа",
    places: "Места",
    objects: "Объекты",
    fun: "Прочее",
  };
  return (
    <div style={{
      marginTop: 8, padding: 8,
      background: "var(--paper-2)", border: "1px solid var(--line)",
      borderRadius: 8, maxHeight: 280, overflowY: "auto",
    }}>
      {Object.keys(byCat).map(cat => (
        <div key={cat} style={{ marginBottom: 10 }}>
          <div style={{
            fontSize: 11, color: "var(--mute)", textTransform: "uppercase",
            letterSpacing: "0.04em", marginBottom: 6,
          }}>
            {catLabels[cat] || cat}
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
            gap: 4,
          }}>
            {byCat[cat].map(def => {
              const isActive = current === `svg:${def.id}`;
              return (
                <button
                  key={def.id}
                  type="button"
                  title={def.name}
                  onClick={() => onPick(`svg:${def.id}`)}
                  style={{
                    aspectRatio: "1 / 1",
                    display: "grid", placeItems: "center",
                    background: isActive ? "rgba(178,52,40,0.10)" : "var(--paper)",
                    border: isActive ? "1px solid var(--brick)" : "1px solid var(--line)",
                    borderRadius: 8, cursor: "pointer",
                    color: isActive ? "var(--brick)" : "var(--ink-2)",
                  }}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d={def.path}/>
                  </svg>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
